import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { dictionaries, isLang, type Copy, type Lang } from './index';

const STORAGE_KEY = 'bp_lang';

interface LangContextValue {
  lang: Lang;
  t: Copy;
  setLang: (lang: Lang) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const LangContext = createContext<LangContextValue | null>(null);

function readInitialLang(): Lang {
  if (typeof window === 'undefined') return 'es';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isLang(stored)) return stored;
  } catch {
    /* localStorage unavailable */
  }
  const navLang = window.navigator.language?.slice(0, 2).toLowerCase();
  return navLang === 'en' ? 'en' : 'es';
}

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(readInitialLang);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  const value = useMemo<LangContextValue>(
    () => ({ lang, t: dictionaries[lang], setLang }),
    [lang, setLang],
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}
